import { type FC, useCallback } from 'react';

import { Alert } from '@vakantiesnl/components/src/atoms/Alert';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { isOfferWithZooverDeal } from '@vakantiesnl/components/src/utils/accommodation/zooverDealHelpers';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { type MatrixOffers } from '@vakantiesnl/services/src/hooks/queries/packageOffers/useGetDepartureDateMatrixOffers';
import { type SearchUrl } from '@vakantiesnl/services/src/hooks/useSearchUrlBuilder';
import { getFormattedRoundedPrice } from '@vakantiesnl/services/src/util/mathUtils';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type MicroCopy, type Search } from '@vakantiesnl/types';
import dayjs from 'dayjs';

import { useCustomStyles } from './PriceMatrix.styles';
import { SkeletonBlock } from '../../atoms/Skeleton/SkeletonBlock';

const MOBILE_MATRIX_COLUMNS_LIMIT = 3;
const TABLET_MATRIX_COLUMNS_LIMIT = 6;

export type PriceMatrixProps = {
	offers: MatrixOffers;
	onClickDepartEarlier: (buttonLabel: string) => void;
	onClickDepartLater: (buttonLabel: string) => void;
	onSelect: (offer: Search.OfferItem) => void;
	microCopies: MicroCopy;
	selected?: string;
	earlierIsSelectable: boolean;
	laterIsSelectable: boolean;
	cheapestOffer: Search.OfferItem | null;
	noOffersLink: SearchUrl;
	onNoOffersLinkClick: VoidFunction;
	invalidPreselection?: boolean;
	carouselRange: number;
	isLoadingOffers: boolean;
	durations: number[];
};

const MatrixItem: FC<{
	offer: Search.OfferItem | null;
	selected?: boolean;
	cheapest?: boolean;
	onSelect?: PriceMatrixProps['onSelect'];
	dataCy?: string;
}> = ({ offer, selected, cheapest, onSelect, dataCy = 'matrix-item' }) => {
	const { classes, cx } = useCustomStyles();

	const handleOnSelect = useCallback(() => {
		/** Don't allow clicks on selected items */
		if (selected) return;

		if (offer && onSelect) onSelect(offer);
	}, [offer, onSelect, selected]);

	if (offer) {
		return (
			<div
				className={cx(classes.matrixItem, {
					[classes.selected]: selected,
					[classes.cheapest]: cheapest && !selected,
				})}
				onClick={handleOnSelect}
				data-cy={dataCy}
			>
				<Typography variant="linkLg" as="span">
					{getFormattedRoundedPrice(offer.pricePerAdult)}
				</Typography>
				<div className={classes.iconContainer}>
					{(offer.hasLuggageIncluded || offer.hasTransferIncluded || isOfferWithZooverDeal(offer)) && (
						<div className={classes.iconIncludingContainer}>
							{isOfferWithZooverDeal(offer) && (
								<Icon
									type="bolt"
									size="small"
									className={classes.dealIcon}
									dataCy="priceMatrix-deal-icon"
								/>
							)}
							{offer.hasTransferIncluded && (
								<Icon type="transfer" size="small" dataCy="priceMatrix-transfer-icon" />
							)}
							{offer.hasLuggageIncluded && (
								<Icon type="luggage" size="small" dataCy="priceMatrix-luggage-icon" />
							)}
						</div>
					)}
				</div>
			</div>
		);
	}

	return <div className={cx(classes.matrixItem, classes.emptyMatrixItem)} data-cy={`${dataCy}-empty`} />;
};

export const PriceMatrix: FC<PriceMatrixProps> = ({
	offers,
	onSelect,
	onClickDepartEarlier,
	onClickDepartLater,
	earlierIsSelectable,
	laterIsSelectable,
	microCopies,
	selected,
	cheapestOffer,
	noOffersLink,
	onNoOffersLinkClick,
	invalidPreselection,
	isLoadingOffers,
	durations,
	carouselRange,
}) => {
	const { classes, cx } = useCustomStyles();
	const { isTablet, isMobile, isTabletOrIsDesktop } = useBreakpoints();

	const alertTitle = inject(microCopies['accommodation.noresults.title.template'], {
		alternativeSearchUrl: (
			<Link {...noOffersLink} className={classes.noOffersLink} onClick={onNoOffersLinkClick}>
				{microCopies['accommodation.noresults.alternativeSearchUrl']}
			</Link>
		),
	});

	const departEarlierButtonLabel = microCopies['accommodation.departEarlier'];
	const departLaterButtonLabel = microCopies['accommodation.departLater'];

	const departEarlierButtonClickHandler = useCallback(() => {
		onClickDepartEarlier(departEarlierButtonLabel);
	}, [departEarlierButtonLabel, onClickDepartEarlier]);

	const departLaterButtonClickHandler = useCallback(() => {
		onClickDepartLater(departLaterButtonLabel);
	}, [departLaterButtonLabel, onClickDepartLater]);

	const fillerOffers = Array(Math.max(carouselRange - Object.entries(offers).length, 0)).fill('');

	return (
		<>
			<div className={classes.container} data-cy="accommodation-PriceMatrix">
				{Object.entries(offers).length === 0 || invalidPreselection ? (
					<Alert
						text={alertTitle}
						variant="warning"
						dataCy="accommodation-NoOffers-alert"
						className={classes.alert}
					/>
				) : (
					<div className={classes.matrixContainer}>
						<div className={classes.itemOffersContainer}>
							<div className={cx(classes.matrixColumn, classes.matrixDurationColumn)}>
								{durations.map((day, index) => (
									<Typography
										variant={isTabletOrIsDesktop ? 'linkMd' : 'linkSm'}
										as="h5"
										className={classes.matrixDuration}
										key={index}
									>
										{day} {microCopies['common.days']}
									</Typography>
								))}
							</div>
							{Object.entries(offers).map(([date, offers], key) => {
								if (
									!offers ||
									(isMobile && key >= MOBILE_MATRIX_COLUMNS_LIMIT) ||
									(isTablet && key >= TABLET_MATRIX_COLUMNS_LIMIT)
								) {
									return;
								}
								const formattedDepartureDate = dayjs(date).format('dd DD MMM');
								return (
									<div key={date} className={classes.matrixColumn} data-cy={`matrixColumn-${date}`}>
										<Typography
											variant={isTabletOrIsDesktop ? 'linkMd' : 'linkSm'}
											as="h5"
											className={classes.matrixDate}
										>
											{formattedDepartureDate}
										</Typography>
										{offers.map((offer, index) => {
											const isCheapest = offer?.flightId === cheapestOffer?.flightId;
											const isSelected = !!selected && offer?.id === selected;
											const duration = offer?.durationDays ?? durations[index];
											return (
												<MatrixItem
													key={index}
													offer={offer}
													selected={isSelected}
													cheapest={isCheapest}
													onSelect={onSelect}
													dataCy={`priceMatrix-item-${date}-${duration}${isSelected ? '-selected' : ''}`}
												/>
											);
										})}
									</div>
								);
							})}
							{fillerOffers.map((_, i) => (
								<div key={i} className={classes.matrixColumn}>
									<Typography
										variant={isTabletOrIsDesktop ? 'linkMd' : 'linkSm'}
										as="h5"
										className={classes.matrixDate}
									>
										<SkeletonBlock width="50px" height="24px" />
									</Typography>
									{durations.map((_, index) => (
										<SkeletonBlock key={index} width="100px" height="70px" />
									))}
								</div>
							))}
						</div>

						<div className={classes.swiftDepartureDates}>
							<>
								<Button
									leadingIcon="chevronLeft"
									variant="tertiary"
									size="small"
									onClick={departEarlierButtonClickHandler}
									disabled={isLoadingOffers || !earlierIsSelectable}
									className={cx({ [classes.hiddenButton]: !earlierIsSelectable })}
									data-cy="depart-earlier"
								>
									{isLoadingOffers ? <SkeletonBlock width="50px" /> : microCopies['common.earlier']}
								</Button>
								<Button
									trailingIcon="chevronRight"
									variant="tertiary"
									size="small"
									onClick={departLaterButtonClickHandler}
									disabled={isLoadingOffers || !laterIsSelectable}
									className={cx({ [classes.hiddenButton]: !laterIsSelectable })}
									data-cy="depart-later"
								>
									{isLoadingOffers ? <SkeletonBlock width="50px" /> : microCopies['common.later']}
								</Button>
							</>
						</div>
					</div>
				)}
			</div>

			{Object.entries(offers).length > 0 && !invalidPreselection && (
				<NoMoreOffers previousAvailable={earlierIsSelectable} nextAvailable={laterIsSelectable} />
			)}
		</>
	);
};

const NoMoreOffers: FC<{ previousAvailable: boolean; nextAvailable: boolean }> = ({
	previousAvailable,
	nextAvailable,
}) => {
	const { classes } = useCustomStyles();
	const microCopies = useMicroCopyContext();

	if (!previousAvailable && !nextAvailable) {
		return (
			<Alert
				variant="info"
				text={microCopies['accommodation.noMorePreviousNextOffers']}
				dataCy="accommodation-noMorePreviousNextOffers-alert"
				className={classes.alert}
			/>
		);
	}

	if (!previousAvailable) {
		return (
			<Alert
				variant="info"
				text={microCopies['accommodation.noMorePreviousOffers']}
				dataCy="accommodation-noMorePreviousOffers-alert"
				className={classes.alert}
			/>
		);
	}

	if (!nextAvailable) {
		return (
			<Alert
				variant="info"
				text={microCopies['accommodation.noMoreNextOffers']}
				dataCy="accommodation-noMoreNextOffers-alert"
				className={classes.alert}
			/>
		);
	}

	return null;
};
